// output_resolution.<field>.priority entries are raw dotted-path strings on
// the wire (app/engines/output_resolver.py's _parse_path), but typing them
// by hand is exactly the "extracted.*.field, this is so frustrating" problem
// reported directly by the user - so the UI never asks anyone to type one.
// Instead each entry is edited as a friendly {source, value} pair via
// PriorityList/PriorityEntryEditor, and only converted to/from the raw
// string at the very edge (here) - the OutputResolutionEntry.priority
// contract sent to the backend is completely unchanged.
//
// The 3 config-authorable forms (confirmed against output_resolver.py):
//   - data.<tag>              reuse another output_resolution field's own resolved value
//   - extracted.*.<field_id>  highest-confidence match across all confirmed documents
//   - request.case_data.<key> always resolves at confidence 1.0
// A 4th form, extracted.<doc_id>.<field_id> (an exact document-instance id),
// is schema-valid but not authorable in config - instance ids only exist at
// request time - so it's never offered as a choice; any pre-existing value
// that doesn't match one of the 3 forms above (this form included) round-
// trips as 'raw', shown read-only rather than silently reinterpreted.
export type PrioritySource = 'document_field' | 'output_field' | 'request_case_data' | 'raw'

export interface PriorityEntryDraft {
  source: PrioritySource
  value: string
}

export const PRIORITY_SOURCE_LABELS: Record<PrioritySource, string> = {
  document_field: "This document's field",
  output_field: 'Another output field',
  request_case_data: 'Data sent with the request',
  raw: 'Advanced / unrecognized value',
}

export const PRIORITY_SOURCE_DESCRIPTIONS: Record<PrioritySource, string> = {
  document_field: 'A value read straight from the uploaded document.',
  output_field: 'Reuse a value already worked out above.',
  request_case_data: 'A value the caller sends with the request, not from a document.',
  raw: "A value saved here that doesn't match any of the 3 forms above - often an incomplete entry (e.g. a field never picked). Edit it directly, or switch to one of the other options above to replace it.",
}

const DOCUMENT_FIELD_RE = /^extracted\.\*\.(.+)$/
const OUTPUT_FIELD_RE = /^data\.(.+)$/
const REQUEST_CASE_DATA_RE = /^request\.case_data\.(.+)$/

export function parsePriorityEntry(path: string): PriorityEntryDraft {
  const documentFieldMatch = DOCUMENT_FIELD_RE.exec(path)
  if (documentFieldMatch) return { source: 'document_field', value: documentFieldMatch[1] }

  const outputFieldMatch = OUTPUT_FIELD_RE.exec(path)
  if (outputFieldMatch) return { source: 'output_field', value: outputFieldMatch[1] }

  const requestCaseDataMatch = REQUEST_CASE_DATA_RE.exec(path)
  if (requestCaseDataMatch) return { source: 'request_case_data', value: requestCaseDataMatch[1] }

  return { source: 'raw', value: path }
}

export function serializePriorityEntry(entry: PriorityEntryDraft): string {
  switch (entry.source) {
    case 'document_field':
      return `extracted.*.${entry.value}`
    case 'output_field':
      return `data.${entry.value}`
    case 'request_case_data':
      return `request.case_data.${entry.value}`
    case 'raw':
      return entry.value
  }
}
